import { Heart, Shield, Award, Star, Globe, MapPin, Sparkles, Users } from 'lucide-react'
import { Helmet } from 'react-helmet-async'
import JsonLd from '../components/JsonLd'
import { BRAND } from '../theme/brand'

export default function About() {
  return (
    <div className="py-24 sm:py-32 min-h-screen" style={{ backgroundColor: BRAND.cream }}>
      <Helmet>
        <title>About Us | 2Life Travel</title>
        <meta
          name="description"
          content="2Life Travel crafts tailormade, premium travel itineraries with personal attention and expert knowledge."
        />
        <link
          rel="canonical"
          href={typeof window !== 'undefined' ? window.location.origin + '/about' : '/about'}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="About Us | 2Life Travel" />
        <meta
          property="og:description"
          content="Trusted South African travel consultants crafting premium, personalized travel experiences."
        />
        <meta
          property="og:url"
          content={typeof window !== 'undefined' ? window.location.href : '/about'}
        />
        <meta property="og:image" content="/image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <JsonLd
        data={[
          {
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: 'About Us',
            url: typeof window !== 'undefined' ? window.location.href : '/about',
            description:
              '2Life Travel has extensive experience creating tailormade travel itineraries for leisure and corporate clients.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'About',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/about',
              },
            ],
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Why Choose 2Life Travel */}
        <div className="mx-auto max-w-3xl text-center mb-20">
          <p
            className="text-sm tracking-[0.3em] uppercase mb-3"
            style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}
          >
            About 2Life Travel
          </p>
          <h2
            className="text-4xl md:text-5xl font-light mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}
          >
            Why Choose <span className="italic font-medium" style={{ color: BRAND.maroon }}>2Life Travel?</span>
          </h2>
          <div className="mx-auto w-16 h-0.5 mb-6" style={{ backgroundColor: BRAND.gold }} />
          <p
            className="text-lg leading-relaxed"
            style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}
          >
            Born from a deep passion for travel and years of exploring the world&apos;s most
            incredible destinations, 2Life Travel specialises in tailormade, premium itineraries for
            discerning leisure travellers. We combine personal attention with up-to-date expertise
            to deliver a stress-free experience from first enquiry to your return home.
          </p>
        </div>

        {/* WHY CHOOSE US */}
        <div className="mx-auto max-w-5xl mb-20">
          <h3
            className="text-2xl md:text-3xl font-light text-center mb-10"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}
          >
            WHY CHOOSE <span className="italic font-medium" style={{ color: BRAND.maroon }}>US</span>
          </h3>

          <div className="flex flex-col sm:flex-row justify-center items-center gap-8">
            <div className="bg-white rounded-xl shadow p-8 flex flex-col items-center text-center w-72">
              <Sparkles className="h-10 w-10 mb-4" style={{ color: BRAND.gold }} />
              <h4
                className="text-lg mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark, fontWeight: 500 }}
              >
                Tailormade
              </h4>
              <p
                className="text-sm"
                style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}
              >
                Every itinerary is designed around your needs, budget, and travel style.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow p-8 flex flex-col items-center text-center w-72">
              <Users className="h-10 w-10 mb-4" style={{ color: BRAND.gold }} />
              <h4
                className="text-lg mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark, fontWeight: 500 }}
              >
                Expert Travel Advisors
              </h4>
              <p
                className="text-sm"
                style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}
              >
                Adriaan and Sonia are avid travellers and bring firsthand insight and expert knowledge to every trip.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow p-8 flex flex-col items-center text-center w-72">
              <Heart className="h-10 w-10 mb-4" style={{ color: BRAND.gold }} />
              <h4
                className="text-lg mb-2"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark, fontWeight: 500 }}
              >
                Personal Attention
              </h4>
              <p
                className="text-sm"
                style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}
              >
                We take care of the details so you can travel with total peace of mind.
              </p>
            </div>
          </div>
        </div>

        {/* Trusted Travel Partner Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center mb-20">
          <div className="rounded-2xl overflow-hidden shadow-lg h-80 bg-gray-100 flex items-center justify-center">
            <img
              src="/customer-service-724x450.jpg"
              alt="Customer service"
              className="object-cover w-full h-full"
            />
          </div>
          <div>
            <div className="mb-4 w-full text-center">
              <span
                className="font-medium italic text-2xl md:text-3xl whitespace-nowrap"
                style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}
              >
                People trust us because they know that we care.
              </span>
            </div>
            <div className="grid grid-cols-2 gap-6">
              <div className="text-center">
                <Star className="h-5 w-5 mx-auto mb-2" style={{ color: BRAND.gold }} />
                <div className="text-3xl font-light mb-1" style={{ color: BRAND.maroon, fontFamily: "'Cormorant Garamond', serif" }}>5.0</div>
                <div className="text-xs tracking-wider uppercase" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}>Google Rating</div>
              </div>
              <div className="text-center">
                <Globe className="h-5 w-5 mx-auto mb-2" style={{ color: BRAND.gold }} />
                <div className="text-3xl font-light mb-1" style={{ color: BRAND.maroon, fontFamily: "'Cormorant Garamond', serif" }}>500+</div>
                <div className="text-xs tracking-wider uppercase" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}>Trips Planned</div>
              </div>
              <div className="text-center">
                <MapPin className="h-5 w-5 mx-auto mb-2" style={{ color: BRAND.gold }} />
                <div className="text-3xl font-light mb-1" style={{ color: BRAND.maroon, fontFamily: "'Cormorant Garamond', serif" }}>150+</div>
                <div className="text-xs tracking-wider uppercase" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}>Countries</div>
              </div>
              <div className="text-center">
                <Heart className="h-5 w-5 mx-auto mb-2" style={{ color: BRAND.gold }} />
                <div className="text-3xl font-light mb-1" style={{ color: BRAND.maroon, fontFamily: "'Cormorant Garamond', serif" }}>100%</div>
                <div className="text-xs tracking-wider uppercase" style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}>Personal Service</div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
